import React, { useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, RefreshCw, Eye } from 'lucide-react';
import { STATUS_COLOURS } from './constants';

const LotRow = ({ lot, onOpen }) => (
  <button
    key={lot._id}
    onClick={() => onOpen(lot._id)}
    className="w-full text-left p-3 flex items-center gap-3 hover:bg-slate-50"
    data-testid={`lot-row-${lot.lot_no}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs sm:text-sm">{lot.lot_no}</span>
        <Badge className={STATUS_COLOURS[lot.status] || ''}>{lot.status}</Badge>
        <span className="text-xs text-slate-500">
          {(lot.pieces_broken ?? 0)}/{lot.piece_count} broken
          {(lot.pieces_draft ?? 0) > 0 && <> · {lot.pieces_draft} draft</>}
          {(lot.pieces_pending ?? 0) > 0 && <> · {lot.pieces_pending} pending</>}
        </span>
      </div>
      <div className="text-xs text-slate-500 mt-0.5 truncate">
        {new Date(lot.created_at).toLocaleDateString()}
      </div>
    </div>
    <Eye className="w-4 h-4 text-slate-400" data-testid={`lot-view-${lot.lot_no}`} />
  </button>
);

const LotSection = ({ title, lots, onOpen, tone, testId, emptyLabel }) => (
  <div className="bg-white rounded-xl shadow border border-amber-100" data-testid={testId}>
    <div className={`px-3 py-2.5 border-b flex items-center gap-2 rounded-t-xl ${tone}`}>
      <h3 className="font-semibold text-sm">{title}</h3>
      <Badge className="bg-white/60 text-slate-800 font-mono">{lots.length}</Badge>
    </div>
    <div className="divide-y">
      {lots.length === 0
        ? <div className="text-center py-5 text-slate-400 text-xs">{emptyLabel}</div>
        : lots.map((l) => <LotRow key={l._id} lot={l} onOpen={onOpen} />)}
    </div>
  </div>
);

const LotsTab = ({
  lots,
  onRefresh, canCreateLot, onCreateLot, onOpenLot,
}) => {
  const { pending, completed } = useMemo(() => {
    const p = [], c = [];
    for (const l of lots) {
      if (l.status === 'completed') c.push(l);
      else p.push(l); // any non-completed (open/…): treat as pending
    }
    return { pending: p, completed: c };
  }, [lots]);

  return (
    <TabsContent value="lots" className="mt-3 space-y-3">
      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow border border-amber-100 flex items-center gap-2 p-3">
        <h2 className="font-semibold text-amber-900 text-sm mr-auto">Lots</h2>
        <Button variant="outline" size="icon" onClick={onRefresh} className="h-10 w-10" data-testid="lot-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
        {canCreateLot && (
          <Button className="h-10 bg-amber-700 hover:bg-amber-800" onClick={onCreateLot} data-testid="create-lot-btn">
            <Plus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Create Lot</span>
          </Button>
        )}
      </div>

      {/* Pending */}
      <LotSection
        title="Pending"
        lots={pending}
        onOpen={onOpenLot}
        tone="bg-amber-50 text-amber-900 border-b-amber-200"
        testId="lots-pending-section"
        emptyLabel="No pending lots"
      />

      {/* Completed */}
      <LotSection
        title="Completed"
        lots={completed}
        onOpen={onOpenLot}
        tone="bg-emerald-50 text-emerald-900 border-b-emerald-200"
        testId="lots-completed-section"
        emptyLabel="No completed lots yet"
      />

      {/* Legacy testid retained for downstream Playwright tests */}
      <div data-testid="lots-table-body" className="hidden" />
    </TabsContent>
  );
};

export default LotsTab;
